import { format } from 'date-fns';
import {
  ComplianceCalendarEvent,
  ComplianceCalendarEventType,
  localEquivalentOfUTC,
} from 'mapistry-shared';
import { sortByStatusThenAlphabetically } from './sortComplianceCalendarEventsHelper';

export const WEEK_VIEW_DAY_COUNT = 7;

export enum WeekRangeDirection {
  Previous = 'previous',
  Next = 'next',
}

export type CalendarEventSection = {
  type: ComplianceCalendarEventType;
  label: string;
  emptyMessage: string;
  allCompletedMessage: string;
};

export const CALENDAR_EVENT_SECTIONS: CalendarEventSection[] = [
  {
    type: ComplianceCalendarEventType.LOG,
    label: 'Log Events',
    emptyMessage: 'No log events due on this day',
    allCompletedMessage: 'All log events completed on this day',
  },
  {
    type: ComplianceCalendarEventType.TASK,
    label: 'Tasks',
    emptyMessage: 'No tasks due on this day',
    allCompletedMessage: 'All tasks completed on this day',
  },
  {
    type: ComplianceCalendarEventType.INSPECTION,
    label: 'Inspections',
    emptyMessage: 'No inspections due on this day',
    allCompletedMessage: 'All inspections completed on this day',
  },
];

export const getEventsForSection = (
  events: ComplianceCalendarEvent[],
  type: ComplianceCalendarEventType,
) =>
  events
    .filter((event) => event.type === type)
    .sort(sortByStatusThenAlphabetically);

export const getSectionEmptyMessage = ({
  section,
  daySummaryEvents,
}: {
  section: CalendarEventSection;
  daySummaryEvents: ComplianceCalendarEvent[];
}) => {
  const summarySectionEvents = getEventsForSection(
    daySummaryEvents,
    section.type,
  );

  return summarySectionEvents.length > 0
    ? section.allCompletedMessage
    : section.emptyMessage;
};

export const shiftUtcDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
};

export const shiftUtcYears = (date: Date, years: number) => {
  const result = new Date(date);
  result.setUTCFullYear(result.getUTCFullYear() + years);
  return result;
};

export const getWeekRangeContaining = (date: Date) => {
  const anchor = new Date(date);
  anchor.setUTCHours(0, 0, 0, 0);

  // snap start to sunday at 00:00 utc of the week that contains the anchor date
  const start = new Date(anchor);
  start.setUTCDate(anchor.getUTCDate() - anchor.getUTCDay());

  const exclusiveEnd = new Date(start);
  exclusiveEnd.setUTCDate(start.getUTCDate() + WEEK_VIEW_DAY_COUNT);

  return { start, exclusiveEnd };
};

export const getMonthRangeContaining = (date: Date) => {
  const start = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1),
  );
  const exclusiveEnd = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1),
  );
  return { start, exclusiveEnd };
};

export const getVisibleRangeTitleParts = (start: Date, exclusiveEnd: Date) => {
  const lastVisibleDay = shiftUtcDays(exclusiveEnd, -1);
  const sameYear = start.getUTCFullYear() === lastVisibleDay.getUTCFullYear();
  const startFormat = sameYear ? 'MMM d' : 'MMM d, yyyy';

  return {
    startText: format(localEquivalentOfUTC(start), startFormat),
    endText: format(localEquivalentOfUTC(lastVisibleDay), 'MMM d, yyyy'),
  };
};

export const getTitle = (start: Date, exclusiveEnd: Date) => {
  const { startText, endText } = getVisibleRangeTitleParts(start, exclusiveEnd);
  return `${startText} – ${endText}`;
};
